// import range from '@/helpers/range'
import vec2 from '@/helpers/vec2'
import range from '@/helpers/range'
import i18next from '@/i18n'

import random from './random'
import utils from './utils'
import { Color, Root, FontStyle } from './constants'

const roots = []

const tmp = vec2.create()
const prng = random.create(0)

function getValue (value) {
  if (Array.isArray(value)) {
    return prng.betweenRange(value, false)
  }
  if (typeof value === 'number') {
    return value
  }
  throw new Error('Invalid root value')
}

function createPoints (def, index, count) {
  const defProgress = index / (count - 1)
  const defAngleProgress = (defProgress - 0.5) * 2
  let angle = index === 0
    ? Math.PI * 0.5 + getValue(def.angle)
    : Math.PI * 0.5 + defAngleProgress + getValue(def.angle)

  const length = getValue(def.length)
  const targetAngle = Math.PI * 0.5
  const points = []
  for (let index = 0; index < def.numPoints; index++) {
    const progress = index / (def.numPoints - 1)
    const inverseProgress = 1 - progress
    const angleRange = def.noise * inverseProgress
    const angleDelta = prng.between(-angleRange, angleRange, false)
    const x = Math.cos(angle + angleDelta) * length * progress
    const y = Math.sin(angle + angleDelta) * length * progress
    angle += (targetAngle - angle) * def.bend
    points.push([x, y])
  }
  return points
}

export function init (cx, { roots: rootDefinitions, numRoots }) {
  const count = getValue(numRoots)
  for (let index = 0; index < count; index++) {
    const defIndex = Math.min(rootDefinitions.length - 1, index)
    const def = rootDefinitions[defIndex]
    prng.reset(def.seed || index)
    const points = createPoints(def, index, count)
    roots.push({
      BBCH: def.BBCH,
      points
    })
  }
}

export function render (cx, data) {
  if (!data) {
    return
  }
  const { RootDepth, BBCH } = data
  const soilLayersHeight = utils.getTopSoilHeight(cx)
  const soilLayersHeightInCm = data.layers.length * 10
  const rootDepthInCm = (RootDepth / 10)
  const relativeRootDepth = rootDepthInCm / soilLayersHeightInCm
  const rootScale = soilLayersHeight
  const rootDepth = rootScale * relativeRootDepth
  cx.save()
  cx.lineCap = 'round'
  cx.lineJoin = 'round'
  let rootIndex = 0
  for (const current of roots) {
    const { points, BBCH: [minBBCH, maxBBCH] } = current
    if (BBCH < minBBCH) {
      continue
    }
    const maxPointIndex = points.length - 1
    const relativeBBCH = range.from(BBCH, minBBCH, maxBBCH)
    const relativeGrowth = (rootIndex === 0 ? 1 : relativeBBCH) * relativeRootDepth
    const relativeRootDepthByPoints = relativeGrowth * maxPointIndex
    const startIndex = Math.floor(relativeRootDepthByPoints)
    const startPointIndex = Math.min(maxPointIndex, startIndex)
    const interpolation = (relativeRootDepthByPoints) - startPointIndex
    const endIndex = Math.ceil(relativeRootDepthByPoints)
    const endPointIndex = Math.min(maxPointIndex, endIndex)
    const rootWidth = Math.min(Root.WIDTH, relativeGrowth * Root.WIDTH)

    for (let index = 0; index < endPointIndex; index++) {
      const progress = (index / endPointIndex)
      const reversedProgress = rootIndex === 0 ? (relativeRootDepth - progress) : (1 - progress)
      const startPoint = points[index]
      const endPoint = points[index + 1]
      if (index === startPointIndex) {
        vec2.linear(tmp, interpolation, startPoint, endPoint)
      } else {
        vec2.copy(tmp, endPoint)
      }
      const [sx, sy] = startPoint
      const [ex, ey] = tmp
      if (index === endPointIndex - 1) {
        cx.lineCap = 'butt'
      }
      cx.lineWidth = range.to(reversedProgress, 0, rootWidth)
      cx.beginPath()
      cx.moveTo(sx * rootScale, sy * rootScale)
      cx.lineTo(ex * rootScale, ey * rootScale)
      cx.strokeStyle = Color.ROOTS
      cx.stroke()
    }

    if (rootIndex === 0 && rootDepthInCm > 1) {
      cx.save()

      cx.lineWidth = 1

      const y = Math.min(rootDepth, soilLayersHeight)

      cx.beginPath()
      cx.moveTo(32, y)
      cx.lineTo((cx.canvas.width >> 2) - 16, y)
      cx.strokeStyle = Color.SECONDARY
      cx.stroke()

      cx.textAlign = 'top'
      cx.textBaseline = 'middle'

      cx.font = FontStyle.EXP
      cx.fillStyle = Color.SECONDARY
      cx.fillText(i18next.t('rooting-depth').toUpperCase(), cx.canvas.width >> 2, y)

      cx.font = FontStyle.VALUE
      cx.fillStyle = Color.PRIMARY
      cx.fillText(`${rootDepthInCm.toFixed(2)}cm`, cx.canvas.width >> 2, y + 16)

      cx.restore()
    }

    rootIndex++
  }

  cx.restore()
}

export function destroy () {
  roots.splice(0, roots.length)
}

export default {
  init,
  render,
  destroy
}
